import React from 'react';
import { Box, Typography, Grid, ListItemIcon, Fade, Grow, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import VerifiedIcon from '@mui/icons-material/Verified';
import GroupIcon from '@mui/icons-material/Group';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import aboutData from '../data/about';

const valueIcons = [
    <LightbulbIcon color="primary" fontSize="large" />,
    <VerifiedIcon color="secondary" fontSize="large" />,
    <EmojiPeopleIcon color="success" fontSize="large" />,
    <GroupIcon color="info" fontSize="large" />,
    <Diversity3Icon color="warning" fontSize="large" />,
];

const polygons = {
    mission: 'polygon(78% 14%, 61% 2%, 34% 2%, 20% 22%, 11% 36%, 5% 48%, 2% 60%, 10% 74%, 23% 86%, 38% 96%, 54% 100%, 70% 96%, 85% 90%, 94% 82%, 98% 70%, 99% 56%, 97% 40%, 90% 26%)',
    foundation: 'polygon(90% 10%, 100% 40%, 95% 80%, 60% 100%, 30% 90%, 10% 60%, 0% 30%, 20% 10%, 50% 0%)',
    values: 'polygon(80% 20%, 100% 50%, 90% 90%, 60% 100%, 30% 90%, 10% 60%, 0% 30%, 20% 10%, 50% 0%)',
    vision: 'polygon(85% 15%, 100% 50%, 90% 90%, 60% 100%, 30% 90%, 10% 60%, 0% 30%, 20% 10%, 50% 0%)',
};

const TimelineSection = ({ history }) => {
    const theme = useTheme();
    return (
        <Box sx={{ py: { xs: 5, md: 8 }, px: { xs: 1, md: 0 }, textAlign: 'center' }}>
            <Fade in timeout={800}>
                <Typography variant="h3" color="primary.main" fontWeight={800} sx={{ mb: 1, fontSize: { xs: '1.4rem', md: '2rem' } }}>{history.title}</Typography>
            </Fade>
            <Fade in timeout={1000}>
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4, fontSize: { xs: '1.05rem', md: '1.2rem' } }}>{history.subtitle}</Typography>
            </Fade>
            <Grid container justifyContent="center" alignItems="center" spacing={4}>
                <Grid item xs={12} md={5}>
                    <Box component="img" src={history.illustration} alt="History Illustration" sx={{ width: '100%', maxWidth: 340, mx: 'auto', mb: { xs: 3, md: 0 } }} />
                </Grid>
                <Grid item xs={12} md={7}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', md: 'flex-start' } }}>
                        {history.content.map((milestone, idx) => (
                            <Box key={milestone.year} sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                                <Box sx={{
                                    minWidth: 60,
                                    fontWeight: 700,
                                    color: theme.palette.primary.main,
                                    fontSize: { xs: '1.1rem', md: '1.2rem' },
                                    mr: 2,
                                    mt: 0.5
                                }}>{milestone.year}</Box>
                                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '1rem', md: '1.1rem' }, textAlign: 'left' }}>{milestone.event}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

const TeamSection = ({ team }) => {
    const theme = useTheme();
    return (
        <Box sx={{ py: { xs: 5, md: 8 }, px: { xs: 1, md: 0 }, textAlign: 'center' }}>
            <Fade in timeout={800}>
                <Typography variant="h3" color="primary.main" fontWeight={800} sx={{ mb: 1, fontSize: { xs: '1.4rem', md: '2rem' } }}>{team.title}</Typography>
            </Fade>
            <Fade in timeout={1000}>
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4, fontSize: { xs: '1.05rem', md: '1.2rem' } }}>{team.subtitle}</Typography>
            </Fade>
            <Grid container justifyContent="center" alignItems="center" spacing={4}>
                <Grid item xs={12} md={5}>
                    <Box component="img" src={team.illustration} alt="Team Illustration" sx={{ width: '100%', maxWidth: 340, mx: 'auto', mb: { xs: 3, md: 0 }, borderRadius: 3, boxShadow: theme.shadows[2] }} />
                </Grid>
                <Grid item xs={12} md={7}>
                    <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '1rem', md: '1.13rem' }, textAlign: 'left' }}>{team.content}</Typography>
                </Grid>
            </Grid>
        </Box>
    );
};

const AboutSection = ({
    section,
    polygon,
    reverse,
    children,
    overlayText,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    return (
        <Box
            sx={{
                py: { xs: 6, md: 8 },
                px: { xs: 1, md: 0 },
                position: 'relative',
                overflow: 'visible',
            }}
        >
            <Grid
                container
                alignItems="center"
                spacing={0}
                sx={{
                    flexDirection: {
                        xs: 'column',
                        md: reverse ? 'row-reverse' : 'row',
                    },
                    position: 'relative',
                }}
            >
                <Grid item xs={12} md={6} sx={{ position: 'relative', zIndex: 1 }}>
                    <Grow in timeout={800} style={{ transitionDelay: '100ms' }}>
                        <Box
                            component="img"
                            src={section.illustration}
                            alt={`${section.title} Illustration`}
                            sx={{
                                width: { xs: '90%', md: '80%' },
                                maxWidth: { xs: 340, md: 420 },
                                minWidth: { md: 260 },
                                marginRight: { md: reverse ? 0 : 3, xs: 0 },
                                marginLeft: { md: reverse ? 3 : 0, xs: 0 },
                                marginBottom: { xs: 3, md: 0 },
                                boxShadow: theme.shadows[4],
                                borderRadius: 3,
                                display: 'block',
                                ...(!isMobile && {
                                    clipPath: polygon,
                                    shapeOutside: polygon,
                                    float: reverse ? 'right' : 'left',
                                    shapeMargin: '24px',
                                }),
                                transition: 'transform 0.3s, box-shadow 0.3s',
                                '&:hover': {
                                    transform: 'scale(1.03)',
                                    boxShadow: theme.shadows[8],
                                },
                                zIndex: 1,
                            }}
                        />
                    </Grow>
                </Grid>
                <Grid item xs={12} md={6} sx={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center' }}>
                    <Fade in timeout={900} style={{ transitionDelay: '200ms' }}>
                        <Box
                            sx={{
                                px: { md: 2, xs: 0 },
                                py: { xs: 1, md: 0 },
                                mt: { xs: -4, md: 0 },
                                background: {
                                    xs: overlayText ? 'rgba(255,255,255,0.92)' : 'transparent',
                                    md: 'transparent',
                                },
                                borderRadius: { xs: 2, md: 0 },
                                boxShadow: { xs: overlayText ? theme.shadows[1] : 'none', md: 'none' },
                                maxWidth: { md: 480 },
                                ml: { md: reverse ? 'auto' : 0 },
                                mr: { md: reverse ? 0 : 'auto' },
                                position: 'relative',
                                zIndex: 2,
                            }}
                        >
                            <Typography
                                variant="h3"
                                color="primary.main"
                                gutterBottom
                                fontWeight={800}
                                sx={{
                                    fontSize: { xs: '1.5rem', md: '2.2rem' },
                                    lineHeight: 1.15,
                                    mb: 1,
                                    textShadow: '0 2px 8px rgba(0,0,0,0.04)',
                                    position: 'relative',
                                    display: 'inline-block',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: -8,
                                        left: 0,
                                        width: '60px',
                                        height: '3px',
                                        background: theme.palette.primary.main,
                                        borderRadius: 2,
                                    },
                                }}
                            >
                                {section.title}
                            </Typography>
                            {section.subtitle && (
                                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2, fontSize: { xs: '1.05rem', md: '1.15rem' } }}>{section.subtitle}</Typography>
                            )}
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{
                                    fontSize: { xs: '1rem', md: '1.13rem' },
                                    lineHeight: 1.7,
                                    textAlign: 'justify',
                                    background: 'none',
                                    borderRadius: 0,
                                    px: 0,
                                    py: 0,
                                    boxShadow: 'none',
                                    mt: 1,
                                }}
                            >
                                {section.content}
                            </Typography>
                            {children}
                        </Box>
                    </Fade>
                </Grid>
            </Grid>
        </Box>
    );
};

const About = () => {
    const theme = useTheme();
    return (
        <Box sx={{ background: theme.palette.background.default, overflow: 'hidden' }}>
            <Box sx={{ textAlign: 'center', mb: 8, mt: 2 }}>
                <Fade in timeout={800}>
                    <Typography
                        variant="h2"
                        color="primary.main"
                        gutterBottom
                        fontWeight={900}
                        sx={{
                            mb: 3,
                            fontSize: { xs: '2.5rem', md: '3.5rem' },
                            letterSpacing: '-1px',
                            position: 'relative',
                            display: 'inline-block',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: -10,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '120px',
                                height: '5px',
                                background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                borderRadius: 3,
                            },
                        }}
                    >
                        About SimpLiTechs
                    </Typography>
                </Fade>
                <Fade in timeout={1000}>
                    <Typography
                        variant="subtitle1"
                        color="text.secondary"
                        sx={{
                            maxWidth: 800,
                            mx: 'auto',
                            fontSize: '1.3rem',
                            lineHeight: 1.7,
                            mt: 2,
                        }}
                    >
                        Pioneering simplicity in complex technology solutions
                    </Typography>
                </Fade>
            </Box>
            {/* Mission Section */}
            <AboutSection
                section={aboutData.mission}
                polygon={polygons.mission}
                reverse={false}
                overlayText={true}
            />
            {/* Foundation Section */}
            <AboutSection
                section={aboutData.foundation}
                polygon={polygons.foundation}
                reverse={true}
                overlayText={true}
            />
            {/* History/Timeline Section */}
            <TimelineSection history={aboutData.history} />
            {/* Values Section */}
            <AboutSection
                section={aboutData.valuesSection}
                polygon={polygons.values}
                reverse={false}
                overlayText={true}
            >
                <Grid container spacing={3} sx={{ mt: 2 }}>
                    {aboutData.values.map((value, idx) => (
                        <Grid item xs={12} sm={6} key={idx}>
                            <Grow in timeout={1000} style={{ transitionDelay: `${idx * 150}ms` }}>
                                <Box
                                    sx={{
                                        p: 2.5,
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 2,
                                        background: theme.palette.mode === 'dark'
                                            ? theme.palette.background.paper
                                            : theme.palette.grey[50],
                                        boxShadow: theme.shadows[1],
                                        transition: 'all 0.3s ease',
                                        height: '100%',
                                        borderLeft: 'none',
                                        '&:hover': {
                                            transform: 'translateY(-3px)',
                                            boxShadow: theme.shadows[3],
                                            background: theme.palette.mode === 'dark'
                                                ? theme.palette.grey[900]
                                                : theme.palette.grey[100],
                                        },
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                                        {valueIcons[idx]}
                                    </ListItemIcon>
                                    <Box>
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={700}
                                            sx={{
                                                mb: 0.5,
                                                fontSize: { xs: '1.05rem', md: '1.13rem' },
                                                color: theme.palette.text.primary,
                                            }}
                                        >
                                            {value.title}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontSize: { xs: '0.93rem', md: '1.01rem' } }}
                                        >
                                            {value.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grow>
                        </Grid>
                    ))}
                </Grid>
            </AboutSection>
            {/* Team Section */}
            <TeamSection team={aboutData.team} />
            {/* Vision Section */}
            <AboutSection
                section={aboutData.vision}
                polygon={polygons.vision}
                reverse={true}
                overlayText={true}
            />
        </Box>
    );
};

export default About;